import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { SecretRecord } from "../SecretRecord";

import { getQueryKey } from "./useOrgSecrets";

const PAGE_SIZE = 100;

/**
 * Every secret in the org, following `next_token` until the last page. For
 * pickers that must offer any secret; use `useOrgSecrets` to page through them.
 */
export function useAllOrgSecrets(
  orgId: string,
  options?: Pick<
    UseQueryOptions<SecretRecord[], Error>,
    "refetchOnWindowFocus"
  >,
) {
  const { secretService } = useDomainServices();

  return useQuery<SecretRecord[], Error>({
    // Under the `orgSecrets` prefix, so creating or deleting a secret refreshes it.
    queryKey: [...getQueryKey(), orgId, "all"],
    queryFn: async ({ signal }) => {
      const secrets: SecretRecord[] = [];
      let nextToken: string | undefined;
      do {
        const page = await secretService.getSecretsForOrg(
          orgId,
          PAGE_SIZE,
          nextToken,
          { abortSignal: signal },
        );
        secrets.push(...page.items);
        nextToken = page.next_token ?? undefined;
      } while (nextToken !== undefined);
      return secrets;
    },
    ...options,
  });
}
